"use strict";
/**
 * 权限码常量与元数据
 * -----------------------------------------------------------------------------
 * **本文件是权限码的唯一真相源。**
 *   · `Permission`            —— 代码里引用（守卫 `@RequirePermission(...)`）
 *   · `PERMISSION_CATALOG`    —— 权限码的展示元数据，种子灌入 `permissions` 表
 *   · `RoleCode`              —— 系统预置角色码
 *   · `PRESET_ROLE_PERMISSIONS` —— 预置角色的初始权限集合（种子用，可在后台调整）
 *
 * 必须与 `docs/04-contracts/权限矩阵.md` §2 完全一致（`pnpm contract:check` 机器校验）。
 *
 * 三方一致性：**代码常量 → 数据库 `permissions` 表 → 后台角色绑定的权限码**。
 * 库里出现代码中不存在的权限码时，绑定不会报错但永远不会生效 ——
 * 这类"静默无效"由 `contract:check` 拦截，不靠人眼。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ROLE_CODE_PATTERN = exports.ADMIN_USERNAME_PATTERN = exports.ADMIN_PASSWORD_POLICY = exports.PRESET_ROLE_PERMISSIONS = exports.SYSTEM_ROLE_META = exports.RoleCode = exports.PERMISSION_CATALOG = exports.Permission = void 0;
exports.Permission = {
    // 看板
    DASHBOARD_VIEW: 'dashboard:view',
    // 报名管理
    REGISTRATION_LIST: 'registration:list',
    REGISTRATION_DETAIL: 'registration:detail',
    REGISTRATION_CREATE: 'registration:create',
    REGISTRATION_EDIT: 'registration:edit',
    REGISTRATION_REVIEW: 'registration:review',
    REGISTRATION_VOID: 'registration:void',
    REGISTRATION_EXPORT: 'registration:export',
    REGISTRATION_IMPORT: 'registration:import',
    // 身份核验
    KYC_VIEW: 'kyc:view',
    KYC_IMAGE: 'kyc:image',
    // 缴费核销
    PAYMENT_LIST: 'payment:list',
    PAYMENT_IMPORT: 'payment:import',
    PAYMENT_RECONCILE: 'payment:reconcile',
    PAYMENT_REVOKE: 'payment:revoke',
    PAYMENT_REMATCH: 'payment:rematch',
    REFUND_CREATE: 'refund:create',
    REFUND_LIST: 'refund:list',
    // 开票登记（一期：人工开具后登记，学员侧无自助开票入口）
    INVOICE_CREATE: 'invoice:create',
    INVOICE_LIST: 'invoice:list',
    // 物流
    LOGISTICS_LIST: 'logistics:list',
    LOGISTICS_SHIP: 'logistics:ship',
    LOGISTICS_UPDATE: 'logistics:update',
    LOGISTICS_PICKUP: 'logistics:pickup',
    // 学员档案
    ARCHIVE_VIEW: 'archive:view',
    ARCHIVE_PII: 'archive:pii',
    // 配置
    CONFIG_BATCH: 'config:batch',
    CONFIG_ENROLLTYPE: 'config:enrolltype',
    CONFIG_FORM: 'config:form',
    CONFIG_ENTRANCE: 'config:entrance',
    CONFIG_THRESHOLD: 'config:threshold',
    NOTIFY_SEND: 'notify:send',
    // 通知台账查询（送达情况 / 消息中心留档）—— 与 `notify:send` 分开：
    // 「查看发送记录」与「触发发送」是两种能力，合并会让只看不发的角色被误授发送权。
    NOTIFY_LIST: 'notify:list',
    SUPPORT_QRCODE: 'support:qrcode',
    // 系统
    SYSTEM_ADMIN: 'system:admin',
    SYSTEM_ROLE: 'system:role',
    SYSTEM_AUDIT: 'system:audit',
    SYSTEM_CONFIG: 'system:config',
    /** 对外 MCP 接入令牌的签发与吊销（口径 C-26） */
    MCP_TOKEN: 'mcp:token',
};
/**
 * 权限码目录 —— 种子灌入 `permissions` 表的唯一来源，也是后台权限矩阵的分组依据。
 * 顺序即展示顺序。
 */
exports.PERMISSION_CATALOG = [
    { code: exports.Permission.DASHBOARD_VIEW, name: '查看看板', module: '看板', sort: 10 },
    { code: exports.Permission.REGISTRATION_LIST, name: '报名单列表', module: '报名管理', sort: 20 },
    {
        code: exports.Permission.REGISTRATION_DETAIL,
        name: '报名单详情（脱敏）',
        module: '报名管理',
        sort: 21,
    },
    { code: exports.Permission.REGISTRATION_CREATE, name: '代学员新建报名单', module: '报名管理', sort: 22 },
    { code: exports.Permission.REGISTRATION_EDIT, name: '编辑报名单字段', module: '报名管理', sort: 23 },
    {
        code: exports.Permission.REGISTRATION_REVIEW,
        name: '后台人工驳回（必填原因）',
        module: '报名管理',
        sort: 24,
    },
    { code: exports.Permission.REGISTRATION_VOID, name: '作废报名单', module: '报名管理', sort: 25 },
    {
        code: exports.Permission.REGISTRATION_EXPORT,
        name: '导出报名数据（脱敏）',
        module: '报名管理',
        sort: 26,
    },
    { code: exports.Permission.REGISTRATION_IMPORT, name: '导入报名数据', module: '报名管理', sort: 27 },
    { code: exports.Permission.KYC_VIEW, name: '查看核验记录与分数', module: '身份核验', sort: 30 },
    { code: exports.Permission.KYC_IMAGE, name: '查看证件影像原图', module: '身份核验', sort: 31 },
    { code: exports.Permission.PAYMENT_LIST, name: '流水列表 / 待认领队列', module: '缴费核销', sort: 40 },
    { code: exports.Permission.PAYMENT_IMPORT, name: '导入流水文件', module: '缴费核销', sort: 41 },
    { code: exports.Permission.PAYMENT_RECONCILE, name: '认领核销（人工）', module: '缴费核销', sort: 42 },
    { code: exports.Permission.PAYMENT_REVOKE, name: '撤销核销', module: '缴费核销', sort: 43 },
    { code: exports.Permission.PAYMENT_REMATCH, name: '手动触发重匹配', module: '缴费核销', sort: 44 },
    { code: exports.Permission.REFUND_CREATE, name: '退费登记', module: '缴费核销', sort: 45 },
    { code: exports.Permission.REFUND_LIST, name: '退费台账', module: '缴费核销', sort: 46 },
    {
        code: exports.Permission.INVOICE_CREATE,
        name: '开票登记（人工开具后登记）',
        module: '开票登记',
        sort: 50,
    },
    { code: exports.Permission.INVOICE_LIST, name: '开票台账', module: '开票登记', sort: 51 },
    { code: exports.Permission.LOGISTICS_LIST, name: '物流列表', module: '物流管理', sort: 60 },
    { code: exports.Permission.LOGISTICS_SHIP, name: '发货登记', module: '物流管理', sort: 61 },
    { code: exports.Permission.LOGISTICS_UPDATE, name: '修改单号 / 状态', module: '物流管理', sort: 62 },
    { code: exports.Permission.LOGISTICS_PICKUP, name: '现场领取台账登记', module: '物流管理', sort: 63 },
    { code: exports.Permission.ARCHIVE_VIEW, name: '查看学员档案（脱敏）', module: '学员档案', sort: 70 },
    {
        code: exports.Permission.ARCHIVE_PII,
        name: '查看完整敏感信息（审计留痕）',
        module: '学员档案',
        sort: 71,
    },
    { code: exports.Permission.CONFIG_BATCH, name: '批次配置', module: '配置', sort: 80 },
    { code: exports.Permission.CONFIG_ENROLLTYPE, name: '报名类型配置', module: '配置', sort: 81 },
    { code: exports.Permission.CONFIG_FORM, name: '表单 Schema 配置与发布', module: '配置', sort: 82 },
    { code: exports.Permission.CONFIG_ENTRANCE, name: '入口注册表配置', module: '配置', sort: 83 },
    { code: exports.Permission.CONFIG_THRESHOLD, name: '准入通过线配置', module: '配置', sort: 84 },
    { code: exports.Permission.NOTIFY_SEND, name: '发送通知', module: '配置', sort: 85 },
    { code: exports.Permission.NOTIFY_LIST, name: '通知台账查询', module: '配置', sort: 86 },
    { code: exports.Permission.SUPPORT_QRCODE, name: '客服码登记管理', module: '配置', sort: 87 },
    { code: exports.Permission.SYSTEM_ADMIN, name: '后台账号管理', module: '系统', sort: 90 },
    { code: exports.Permission.SYSTEM_ROLE, name: '角色与权限配置', module: '系统', sort: 91 },
    { code: exports.Permission.SYSTEM_AUDIT, name: '审计日志查询', module: '系统', sort: 92 },
    { code: exports.Permission.SYSTEM_CONFIG, name: '系统配置（SysConfig）', module: '系统', sort: 93 },
    { code: exports.Permission.MCP_TOKEN, name: 'MCP 接入令牌管理', module: '系统', sort: 94 },
];
/** 系统预置角色码 */
exports.RoleCode = {
    ADMIN: 'admin',
    REVIEWER: 'reviewer',
    FINANCE: 'finance',
    LOGISTICS: 'logistics',
    SUPPORT: 'support',
    OPERATOR: 'operator',
};
/**
 * 系统预置角色。`isSystem = true` 的含义见 `权限矩阵.md` §1.1：
 * 不可删除、不可改 `code`，但**权限码集合可在后台调整**（因此本表的权限集合只是初始值）。
 */
exports.SYSTEM_ROLE_META = [
    {
        code: exports.RoleCode.ADMIN,
        name: '超级管理员',
        description: '全权限，含账号管理与角色权限配置',
        sort: 10,
    },
    {
        code: exports.RoleCode.REVIEWER,
        name: '审核员',
        description: '报名单人工驳回、核验结果复核',
        sort: 20,
    },
    {
        code: exports.RoleCode.FINANCE,
        name: '财务',
        description: '流水导入、核销认领、退费登记、开票登记',
        sort: 30,
    },
    {
        code: exports.RoleCode.LOGISTICS,
        name: '物流专员',
        description: '发货登记、单号维护、现场领取台账',
        sort: 40,
    },
    {
        code: exports.RoleCode.SUPPORT,
        name: '客服',
        description: '查询学员档案、消息查看、客服码查看',
        sort: 50,
    },
    {
        code: exports.RoleCode.OPERATOR,
        name: '运营',
        description: '批次、报名类型、表单、入口、通知配置',
        sort: 60,
    },
];
/**
 * 系统预置角色的**初始**权限码集合（种子数据使用）。
 * 与 `docs/04-contracts/权限矩阵.md` §2 的勾选一致。
 *
 * ⚠️ 这**不是**运行时的权限来源 —— 运行时的权限一律从数据库读（`role_permissions`）。
 * 后台调整过角色权限后，本表与库内实际值不一致是**正常**的：本表只负责"首次灌种子"。
 */
exports.PRESET_ROLE_PERMISSIONS = {
    admin: Object.values(exports.Permission),
    reviewer: [
        exports.Permission.DASHBOARD_VIEW,
        exports.Permission.REGISTRATION_LIST,
        exports.Permission.REGISTRATION_DETAIL,
        exports.Permission.REGISTRATION_REVIEW,
        exports.Permission.KYC_VIEW,
        exports.Permission.KYC_IMAGE,
        exports.Permission.ARCHIVE_VIEW,
    ],
    finance: [
        exports.Permission.DASHBOARD_VIEW,
        exports.Permission.REGISTRATION_LIST,
        exports.Permission.REGISTRATION_DETAIL,
        exports.Permission.PAYMENT_LIST,
        exports.Permission.PAYMENT_IMPORT,
        exports.Permission.PAYMENT_RECONCILE,
        exports.Permission.PAYMENT_REVOKE,
        exports.Permission.PAYMENT_REMATCH,
        exports.Permission.REFUND_CREATE,
        exports.Permission.REFUND_LIST,
        exports.Permission.INVOICE_CREATE,
        exports.Permission.INVOICE_LIST,
    ],
    logistics: [
        exports.Permission.DASHBOARD_VIEW,
        exports.Permission.REGISTRATION_LIST,
        exports.Permission.REGISTRATION_DETAIL,
        exports.Permission.LOGISTICS_LIST,
        exports.Permission.LOGISTICS_SHIP,
        exports.Permission.LOGISTICS_UPDATE,
        exports.Permission.LOGISTICS_PICKUP,
    ],
    support: [
        exports.Permission.DASHBOARD_VIEW,
        exports.Permission.REGISTRATION_LIST,
        exports.Permission.REGISTRATION_DETAIL,
        exports.Permission.LOGISTICS_LIST,
        exports.Permission.ARCHIVE_VIEW,
    ],
    operator: [
        exports.Permission.DASHBOARD_VIEW,
        exports.Permission.REGISTRATION_LIST,
        exports.Permission.REGISTRATION_DETAIL,
        exports.Permission.REGISTRATION_CREATE,
        exports.Permission.REGISTRATION_EDIT,
        exports.Permission.REGISTRATION_EXPORT,
        exports.Permission.REGISTRATION_IMPORT,
        exports.Permission.CONFIG_BATCH,
        exports.Permission.CONFIG_ENROLLTYPE,
        exports.Permission.CONFIG_FORM,
        exports.Permission.CONFIG_ENTRANCE,
        exports.Permission.NOTIFY_SEND,
        exports.Permission.NOTIFY_LIST,
        exports.Permission.SUPPORT_QRCODE,
    ],
};
/** 账号/口令相关常量（服务端与后台提示共用，避免两边各写一份） */
exports.ADMIN_PASSWORD_POLICY = {
    /** 最小长度。10 位是 NIST SP 800-63B 对「单一因素口令」的常见落地值 */
    MIN_LENGTH: 10,
    /** 最大长度：scrypt 无 72 字节截断问题，仅防超长入参打满 CPU */
    MAX_LENGTH: 128,
};
/** 用户名格式：字母开头，3–32 位，允许数字与 `_ . -` */
exports.ADMIN_USERNAME_PATTERN = '^[a-zA-Z][a-zA-Z0-9_.-]{2,31}$';
/** 自定义角色码格式：小写字母开头，2–32 位 */
exports.ROLE_CODE_PATTERN = '^[a-z][a-z0-9_-]{1,31}$';
//# sourceMappingURL=permissions.js.map